package com.patronat.top.dto

import kotlinx.datetime.Instant
import kotlinx.serialization.Serializable

@Serializable
data class CreateReviewRequest(
    val rating: Int,
    val details: ReviewRatingDetails? = null,
    val text: String? = null,
    val photos: List<ReviewMediaRef> = emptyList(),
    val photoMediaIds: List<String> = emptyList()
)

@Serializable
data class ReviewModerationRequest(
    val approved: Boolean
)

@Serializable
data class ReviewOwnerReplyRequest(
    val text: String
)

@Serializable
data class ReviewMediaRef(
    val mediaId: String? = null,
    val publicUrl: String? = null
)

@Serializable
enum class ReviewStatus {
    ON_MODERATION,
    PUBLISHED,
    REJECTED
}

@Serializable
enum class ReviewAiStatus {
    PENDING,
    PROCESSING,
    DONE,
    ERROR
}

@Serializable
enum class ReviewAiDecision {
    PASS,
    WARN,
    FAIL
}

@Serializable
enum class ReviewAiSeverity {
    WARNING,
    CRITICAL
}

@Serializable
enum class ReviewAiSource {
    REKOGNITION,
    BEDROCK,
    SYSTEM
}

@Serializable
data class ReviewAiFinding(
    val fieldPath: String,
    val severity: ReviewAiSeverity,
    val reason: String,
    val confidence: Double? = null,
    val source: ReviewAiSource
)

@Serializable
data class ReviewOwnerReplyResponse(
    val text: String,
    val repliedAt: Instant,
    val repliedBy: String
)

@Serializable
data class ReviewAuthorResponse(
    val displayName: String? = null,
    val avatarUrl: String? = null
)

@Serializable
data class ReviewRatingDetails(
    val price: Int? = null,
    val quality: Int? = null,
    val speed: Int? = null,
    val staff: Int? = null,
    val atmosphere: Int? = null
)

@Serializable
data class ReviewResponse(
    val id: String,
    val venueId: String,
    val clientId: String,
    val author: ReviewAuthorResponse? = null,
    val rating: Int,
    val details: ReviewRatingDetails? = null,
    val text: String? = null,
    val photos: List<ReviewMediaRef> = emptyList(),
    val photoMediaIds: List<String> = emptyList(),
    val status: ReviewStatus,
    val aiStatus: ReviewAiStatus? = null,
    val aiDecision: ReviewAiDecision? = null,
    val aiFindings: List<ReviewAiFinding> = emptyList(),
    val aiCompletedAt: Instant? = null,
    val aiError: String? = null,
    val moderatedAt: Instant? = null,
    val moderatedBy: String? = null,
    val ownerReply: ReviewOwnerReplyResponse? = null,
    val likesCount: Long = 0,
    val isLikedByMe: Boolean = false,
    val createdAt: Instant,
    val updatedAt: Instant
)

@Serializable
data class ReviewListResponse(
    val items: List<ReviewResponse>,
    val totalCount: Long,
    val page: Int,
    val pageSize: Int
)

@Serializable
data class ReviewFilterRequest(
    val page: Int = 1,
    val pageSize: Int = 20,
    val venueId: String? = null,
    val status: ReviewStatus? = null,
    val query: String? = null,
    val minRating: Int? = null,
    val maxRating: Int? = null,
    val aiStatuses: List<ReviewAiStatus> = emptyList(),
    val aiDecisions: List<ReviewAiDecision> = emptyList()
)

@Serializable
data class ReviewRatingBucketResponse(
    val rating: Int,
    val count: Long
)

@Serializable
data class ReviewSummaryResponse(
    val venueId: String,
    val averageRating: Double,
    val totalReviews: Long,
    val averageDetails: ReviewRatingDetailsAverage? = null,
    val distribution: List<ReviewRatingBucketResponse>
)

@Serializable
data class ReviewRatingDetailsAverage(
    val price: Double? = null,
    val quality: Double? = null,
    val speed: Double? = null,
    val staff: Double? = null,
    val atmosphere: Double? = null
)
